import React, { useState, useEffect, useMemo } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiMapPin, FiGlobe, FiList } from 'react-icons/fi';
import apiAxios from 'helpers/apiAxios';
import { CHART_FONT, CHART_TOOLTIP } from 'helpers/echartsTheme';
import ContractDrillDownPanel from './ContractDrillDownPanel';
import { countryName } from './countryNames';

const COLORS = ['#3B82F6','#6366F1','#10B981','#F59E0B','#EF4444','#A855F7','#06B6D4','#F97316'];

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MT`;
const fmt  = (n) => Number(n || 0).toLocaleString('en-US');

const BAR_METRICS = [
  { key: 'contract_count',  label: 'Count',   fmt: v => v },
  { key: 'contract_value',  label: 'Value $', fmt: fmtM },
  { key: 'contract_weight', label: 'Weight',  fmt: fmtW },
];

const SortableTh = ({ label, sortKey, currentSort, onSort, align = 'right' }) => {
  const active = currentSort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`tw-px-3 tw-py-2 tw-text-${align} tw-text-xs tw-font-semibold tw-text-gray-600 tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none tw-whitespace-nowrap`}
    >
      {label}{active && <span className="tw-ml-1 tw-text-gray-400">{currentSort.dir === 'desc' ? '▼' : '▲'}</span>}
    </th>
  );
};

SortableTh.propTypes = {
  label: PropTypes.node.isRequired,
  sortKey: PropTypes.string.isRequired,
  currentSort: PropTypes.shape({
    key: PropTypes.string,
    dir: PropTypes.oneOf(['asc', 'desc']),
  }).isRequired,
  onSort: PropTypes.func.isRequired,
  align: PropTypes.oneOf(['left', 'right', 'center']),
};

const ContractProvinceChart = ({ data: initialData, loading: initialLoading, error: initialError, baseParams }) => {
  const [selectedCountry, setSelectedCountry] = useState('');
  const [filteredData,    setFilteredData]    = useState(null);
  const [filtering,       setFiltering]       = useState(false);
  const [metric,          setMetric]          = useState('contract_count');
  const [view,            setView]            = useState('list');
  const [drillOpen,       setDrillOpen]       = useState(false);
  const [drillTitle,      setDrillTitle]      = useState('');
  const [drillExtra,      setDrillExtra]      = useState({});
  const [tableSort,       setTableSort]       = useState({ key: 'contract_count', dir: 'desc' });

  const displayData = filteredData !== null ? filteredData : (initialData || []);

  const countries = useMemo(() => {
    const all = (initialData || []).map(r => r.country).filter(Boolean);
    return [...new Set(all)].sort();
  }, [initialData]);

  useEffect(() => {
    if (!selectedCountry || !baseParams) { setFilteredData(null); return; }
    let cancelled = false;
    setFiltering(true);
    apiAxios.get('/crm/dashboard/contracts/get_province_data', {
      params: { ...baseParams, country: selectedCountry }
    }).then(res => {
      if (!cancelled) setFilteredData(res.data?.data || []);
    }).catch(() => {
      if (!cancelled) setFilteredData([]);
    }).finally(() => {
      if (!cancelled) setFiltering(false);
    });
    return () => { cancelled = true; };
  }, [selectedCountry, baseParams]);

  if (initialLoading && !filteredData) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (initialError) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading province data</p>
    </Card>
  );

  const sorted    = [...displayData].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  const maxVal    = Math.max(...sorted.map(r => r[metric] || 0), 1);
  const activeFmt = (BAR_METRICS.find(m => m.key === metric) || BAR_METRICS[0]).fmt;

  const tableRows = [...displayData].sort((a, b) => {
    const diff = (b[tableSort.key] || 0) - (a[tableSort.key] || 0);
    return tableSort.dir === 'desc' ? diff : -diff;
  });

  const totalContracts = tableRows.reduce((s, r) => s + (r.contract_count  || 0), 0);
  const totalCtrVal    = tableRows.reduce((s, r) => s + (r.contract_value  || 0), 0);
  const totalWeight    = tableRows.reduce((s, r) => s + (r.contract_weight || 0), 0);

  const handleSort = (key) => {
    setTableSort(prev =>
      prev.key === key
        ? { ...prev, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' }
    );
  };

  const handleClick = (row) => {
    setDrillTitle(`Province: ${row.province} (${row.country})`);
    setDrillExtra({ province: row.province, country: row.country });
    setDrillOpen(true);
  };

  const barOption = {
    textStyle: { fontFamily: CHART_FONT.fontFamily },
    grid: { left: 8, right: 84, top: 4, bottom: 4, containLabel: true },
    tooltip: {
      ...CHART_TOOLTIP,
      formatter: (p) => {
        const row = sorted[p.dataIndex];
        if (!row) return '';
        return [
          `<b>${escapeHtml(row.province)}</b> <span style="color:#9CA3AF">${escapeHtml(countryName(row.country))}</span>`,
          `${(BAR_METRICS.find(m => m.key === metric) || BAR_METRICS[0]).label}: <b>${escapeHtml(String(activeFmt(row[metric] || 0)))}</b>`,
          `<span style="color:#2563EB;font-weight:600">${row.contract_count}C</span> · ${escapeHtml(fmtM(row.contract_value))}`
        ].join('<br/>');
      }
    },
    xAxis: { type: 'value', show: false, max: maxVal },
    yAxis: {
      type: 'category',
      inverse: true,
      data: sorted.map(r => `${r.province}|${r.country}`),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        ...CHART_FONT,
        formatter: (value, index) => {
          const [province, country] = value.split('|');
          return `{rank|${index + 1}} {name|${province}}\n{country|${countryName(country)}}`;
        },
        rich: {
          rank:    { color: '#9CA3AF', fontSize: 11, width: 16, align: 'right' },
          name:    { color: '#1F2937', fontWeight: 600, fontSize: 11, align: 'left', width: 96, overflow: 'truncate' },
          country: { color: '#9CA3AF', fontSize: 10, padding: [2, 0, 0, 20], align: 'left' }
        }
      }
    },
    series: [{
      type: 'bar',
      barWidth: 20,
      cursor: 'pointer',
      showBackground: true,
      backgroundStyle: { color: '#F3F4F6', borderRadius: 10 },
      itemStyle: { borderRadius: 10, opacity: 0.8 },
      label: {
        show: true,
        position: 'right',
        fontSize: 11,
        fontWeight: 'bold',
        color: 'inherit',
        formatter: (p) => String(activeFmt(sorted[p.dataIndex]?.[metric] || 0))
      },
      data: sorted.map((r, i) => ({
        value: r[metric] || 0,
        itemStyle: { color: COLORS[i % COLORS.length] }
      }))
    }]
  };

  return (
    <>
      <Card className="tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm">
        <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-2">
            <FiMapPin size={16} className="tw-text-red-500" />
            <h3 className="tw-font-bold tw-text-gray-900">By Province</h3>
            <span className="tw-text-xs tw-text-gray-400">
              {filtering ? 'Loading…' : `Top ${sorted.length}`}
            </span>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            {/* Country filter */}
            <div className="tw-relative tw-flex tw-items-center tw-gap-1">
              <FiGlobe size={12} className="tw-text-gray-400" />
              <select
                value={selectedCountry}
                onChange={e => setSelectedCountry(e.target.value)}
                className="tw-text-xs tw-border tw-border-gray-200 tw-rounded tw-px-2 tw-py-1 tw-bg-white tw-text-gray-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-cursor-pointer"
              >
                <option value="">All Countries</option>
                {countries.map(c => <option key={c} value={c}>{countryName(c)}</option>)}
              </select>
            </div>

            {/* View toggle */}
            <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
              <button
                onClick={() => setView('list')}
                className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 ${view === 'list' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}
              >
                Chart
              </button>
              <button
                onClick={() => setView('table')}
                className={`tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 tw-border-l tw-border-l-gray-200 ${view === 'table' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}
              >
                <FiList size={11} /> Table
              </button>
            </div>

            {/* Metric toggle — chart view only */}
            {view === 'list' && (
              <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
                {BAR_METRICS.map((opt, idx) => (
                  <button key={opt.key} onClick={() => setMetric(opt.key)}
                    className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 ${idx > 0 ? 'tw-border-l tw-border-l-gray-200' : ''} ${metric === opt.key ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Bar / chart view ── */}
        {view === 'list' && (
          <>
            <div className="tw-p-4 tw-max-h-80 tw-overflow-y-auto tw-relative">
              {filtering && (
                <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/70 tw-z-10">
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {sorted.length === 0 ? (
                <p className="tw-text-gray-400 tw-text-center tw-py-8">No data available</p>
              ) : (
                <ReactECharts
                  option={barOption}
                  style={{ height: Math.max(sorted.length * 34 + 8, 120) }}
                  notMerge
                  onEvents={{
                    click: (params) => {
                      const row = sorted[params.dataIndex];
                      if (row) handleClick(row);
                    }
                  }}
                />
              )}
            </div>
            <div className="tw-px-5 tw-py-2 tw-border-t tw-border-gray-100 tw-text-xs tw-text-gray-400 tw-text-center">
              Click any row to view detailed contracts
            </div>
          </>
        )}

        {/* ── Detail table view ── */}
        {view === 'table' && (
          <div className="tw-overflow-auto tw-max-h-[460px] tw-relative">
            {filtering && (
              <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/70 tw-z-20">
                <LoadingSpinner size="sm" />
              </div>
            )}
            <table className="tw-w-full tw-text-xs tw-border-collapse">
              <thead>
                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10">
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-8">#</th>
                  <SortableTh label="Province"    sortKey="province"        currentSort={tableSort} onSort={handleSort} align="left" />
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600">Country</th>
                  <SortableTh label="Contracts"   sortKey="contract_count"  currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Contract $"  sortKey="contract_value"  currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Weight (MT)" sortKey="contract_weight" currentSort={tableSort} onSort={handleSort} />
                  <th className="tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-400"></th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, i) => (
                  <tr
                    key={`${row.province}-${row.country}`}
                    onClick={() => handleClick(row)}
                    className={`tw-border-b tw-border-gray-100 tw-cursor-pointer ${i % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/40'} hover:tw-bg-blue-50/60 tw-transition-colors`}
                  >
                    <td className="tw-px-3 tw-py-2 tw-text-gray-400 tw-font-mono">{i + 1}</td>
                    <td className="tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-800 tw-whitespace-nowrap">{row.province}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-gray-500">{countryName(row.country)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-blue-700">{fmt(row.contract_count)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-blue-700">{fmtM(row.contract_value)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{fmtW(row.contract_weight)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-blue-400">↗</td>
                  </tr>
                ))}
              </tbody>
              {tableRows.length > 0 && (
                <tfoot>
                  <tr className="tw-border-t-2 tw-border-gray-300 tw-bg-gray-100 tw-font-bold">
                    <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-text-xs" colSpan={3}>Total ({tableRows.length} provinces)</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-blue-700">{fmt(totalContracts)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-blue-700">{fmtM(totalCtrVal)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{fmtW(totalWeight)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>

      <ContractDrillDownPanel
        open={drillOpen}
        title={drillTitle}
        baseParams={baseParams || {}}
        extraParams={drillExtra}
        onClose={() => setDrillOpen(false)}
      />
    </>
  );
};

ContractProvinceChart.propTypes = {
  data:       PropTypes.array,
  loading:    PropTypes.bool,
  error:      PropTypes.any,
  baseParams: PropTypes.object
};
ContractProvinceChart.defaultProps = { data: [], loading: false, error: null, baseParams: {} };

export default ContractProvinceChart;
